import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const DISMISS_KEY = "hf_pwa_dismissed_at";
const DISMISS_DAYS = 3; // re-prompt every 3 days

function isRunningStandalone() {
  return (
    (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: fullscreen)").matches) ||
    (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    (typeof navigator !== "undefined" && navigator.standalone === true)
  );
}
function isIOS() {
  return typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent);
}
function isAndroid() {
  return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
}
function recentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return (Date.now() - parseInt(v, 10)) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isRunningStandalone()) return;
    if (recentlyDismissed()) return;

    // Android / Chrome flow
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setOpen(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari — no beforeinstallprompt; show manual instructions after a short delay
    if (isIOS() && !isRunningStandalone()) {
      const t = setTimeout(() => setShowIOS(true), 8000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch { /* noop */ }
    setDeferred(null);
    setOpen(false);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setOpen(false);
    setShowIOS(false);
  };

  if (isRunningStandalone()) return null;

  return (
    <AnimatePresence>
      {(open || showIOS) && (
        <motion.div
          data-testid="pwa-install-prompt"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", damping: 22 }}
          className="fixed bottom-24 inset-x-3 z-[65] max-w-md md:left-1/2 md:-translate-x-1/2 md:right-auto md:w-full rounded-2xl bg-gradient-to-br from-[#FF2A5F]/95 via-[#A855F7]/95 to-[#00E5FF]/95 shadow-2xl backdrop-blur border border-white/20 p-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-black/30 flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-6 h-6 text-white"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-outfit font-extrabold text-sm text-white">Install Hifireels for full-screen</div>
              <p className="text-[11px] text-white/85 mt-0.5 leading-tight">
                {isIOS()
                  ? <>Tap <b className="text-[#FF7A00]">Share ▲</b> in Safari, then <b className="text-[#FF7A00]">Add to Home Screen</b>. Enjoy edge-to-edge reels without browser bars.</>
                  : <>Get a real app experience — hides all browser bars & notification bar. One-tap install.</>
                }
              </p>
            </div>
            <button onClick={dismiss} data-testid="pwa-dismiss" className="p-1 rounded-full hover:bg-white/10 flex-shrink-0">
              <X className="w-4 h-4 text-white"/>
            </button>
          </div>
          {!isIOS() && deferred && (
            <button onClick={install} data-testid="pwa-install-btn"
              className="mt-2.5 w-full py-2.5 rounded-full bg-black text-white text-sm font-extrabold flex items-center justify-center gap-1.5 active:scale-95">
              <Download className="w-4 h-4"/>Install Hifireels
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export { isRunningStandalone, isIOS, isAndroid };
